/* istanbul ignore file */
import React, { type FC, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material'

import { ConnectwareError, ConnectwareErrorType, Translation } from '../../../domain'

import { Autocomplete, type DropdownSize, type WithIcon, type WithMeta } from '../common'
import { DefaultIcon } from './DefaultIcon'
import { SplitButtonControl } from './SplitButtonControl'

type Option = Readonly<{ id: string, label: string }>
type ExampleProps = Readonly<{ size: DropdownSize }>

const options: readonly Option[] = [
    { id: 'apple', label: 'Apple' },
    { id: 'banana', label: 'Banana' },
    { id: 'cherry', label: 'Cherry' },
    { id: 'date', label: 'Date' },
    { id: 'elderberry', label: 'Elderberry' },
    { id: 'fig', label: 'Fig' },
    { id: 'grape', label: 'Grape' },
]

const getOptionLabel = (option: Option): string => option.label
const isOptionEqualToValue = (a: Option, b: Option): boolean => a.id === b.id
const isOptionDisabled = (option: Option): boolean => option.id === 'banana'

const iconOptions: readonly WithIcon<Option>[] = options.map((option) => ({ ...option, icon: <DefaultIcon /> }))
const iconMetaOptions: readonly WithMeta<WithIcon<Option>>[] = options.map((option) => ({
    ...option,
    icon: <DefaultIcon />,
    meta: `${option.label} is a fruit`,
}))

const metaLabeledOptions: readonly WithMeta<Option>[] = options.map((option) => ({ ...option, meta: `${option.label} is a fruit` }))

const statuses = ['success', 'warning', 'error'] as const
const statusOptions: readonly WithMeta<Option>[] = options.map((option, index) => ({
    ...option,
    status: statuses[index % statuses.length],
    meta: ['Online', 'Degraded', 'Offline'][index % statuses.length],
}))
const metaOptions: readonly WithMeta<Option>[] = options.map((option, index) => ({ ...option, meta: `#${index + 1}` }))

const loadingError = new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Something went wrong while loading the options.')

const sizeOptions: readonly DropdownSize[] = ['small', 'medium']
const exampleStyle = { width: 280, maxWidth: '100%' } as const

const SingleSelect: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<WithMeta<Option> | null>(null)

    return (
        <Autocomplete
            size={size}
            options={metaLabeledOptions}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const MultiSelect: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<readonly Option[]>([])

    return (
        <Autocomplete
            multiple
            size={size}
            options={options}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={[...value]}
            onSelect={setValue}
        />
    )
}

const MultiSelectWithIcon: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<readonly WithIcon<Option>[]>([])

    return (
        <Autocomplete
            multiple
            size={size}
            options={iconOptions}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={[...value]}
            onSelect={setValue}
        />
    )
}

const MultiSelectWithIconAndMeta: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<readonly WithMeta<WithIcon<Option>>[]>([iconMetaOptions[0]!])

    return (
        <Autocomplete
            multiple
            size={size}
            options={iconMetaOptions}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={[...value]}
            onSelect={setValue}
        />
    )
}

const SingleSelectWithStatus: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<WithMeta<Option> | null>(null)

    return (
        <Autocomplete
            size={size}
            options={statusOptions}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const SingleSelectWithMeta: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<WithMeta<Option> | null>(null)

    return (
        <Autocomplete
            size={size}
            options={metaOptions}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const WithDisabledOption: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<Option | null>(null)

    return (
        <Autocomplete
            size={size}
            options={options}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionDisabled={isOptionDisabled}
            value={value}
            onSelect={setValue}
        />
    )
}

const FreeSolo: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<Option | string | null>(null)

    return (
        <Autocomplete
            freeSolo
            size={size}
            options={options}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={(option: Option | string) => (typeof option === 'string' ? option : option.label)}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const MultiFreeSolo: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<readonly string[]>([])

    return <Autocomplete multiple freeSolo size={size} options={[]} noOptionsText={Translation.NO_ENTRIES_AVAILABLE} value={[...value]} onSelect={setValue} />
}

const DisableClearable: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<Option>(options[0]!)

    return (
        <Autocomplete
            disableClearable
            size={size}
            options={options}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const Loading: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<Option | null>(null)

    return (
        <Autocomplete
            size={size}
            options={null}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const ErrorState: FC<ExampleProps> = ({ size }) => {
    const [value, setValue] = useState<Option | null>(null)

    return (
        <Autocomplete
            size={size}
            options={loadingError}
            noOptionsText={Translation.NO_ENTRIES_AVAILABLE}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={value}
            onSelect={setValue}
        />
    )
}

const examples: readonly Readonly<{ label: string, Example: FC<ExampleProps> }>[] = [
    { label: 'Single select', Example: SingleSelect },
    { label: 'Multi select', Example: MultiSelect },
    { label: 'Multi select with icon', Example: MultiSelectWithIcon },
    { label: 'Multi select with icon and meta', Example: MultiSelectWithIconAndMeta },
    { label: 'Single select with status dot', Example: SingleSelectWithStatus },
    { label: 'Single select with meta', Example: SingleSelectWithMeta },
    { label: 'With a disabled option', Example: WithDisabledOption },
    { label: 'Free solo', Example: FreeSolo },
    { label: 'Multi select + Free solo', Example: MultiFreeSolo },
    { label: 'Disable clearable', Example: DisableClearable },
    { label: 'Loading', Example: Loading },
    { label: 'Error', Example: ErrorState },
]

const Autocompletes: FC = () => {
    const [size, setSize] = useState<DropdownSize>('medium')

    return (
        <>
            <Typography variant="subtitle1" sx={{ display: 'block', mb: 4 }}>
                Autocomplete
            </Typography>

            <SplitButtonControl
                ariaLabel="Autocomplete size"
                testIdPrefix="autocomplete-storybook-size"
                option={size}
                options={sizeOptions}
                onChange={setSize}
            />

            <Grid container spacing={6}>
                {examples.map(({ label, Example }) => (
                    <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            {label}
                        </Typography>
                        <Box sx={exampleStyle}>
                            <Example size={size} />
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </>
    )
}

export { Autocompletes }
